import React from 'react'
import { Copy, Check } from 'lucide-react'
import { Highlight, themes } from 'prism-react-renderer'
import type { Post, CodeBlock } from './posts'

// ─── Code block ───────────────────────────────────────────────────────────────

export const CodeSnippet: React.FC<{ block: CodeBlock }> = ({ block }) => {
  const [copied, setCopied] = React.useState(false)

  const copy = () => {
    navigator.clipboard.writeText(block.code).then(() => {
      setCopied(true)
      setTimeout(() => setCopied(false), 2000)
    })
  }

  return (
    <div className="my-4 rounded-lg overflow-hidden border border-jax-navy">
      <div className="flex items-center justify-between px-4 py-2 bg-jax-surface border-b border-jax-navy">
        <div className="flex items-center gap-2.5">
          <span className="font-mono text-[10px] text-jax-subtle tracking-widest uppercase">
            {block.language}
          </span>
          {block.label && (
            <>
              <span className="text-jax-navy">·</span>
              <span className="font-mono text-[10px] text-jax-subtle">{block.label}</span>
            </>
          )}
        </div>
        <button
          onClick={copy}
          className="flex items-center gap-1.5 text-[10px] font-mono text-jax-subtle hover:text-jax-link transition-colors duration-150"
        >
          {copied
            ? <><Check className="w-3 h-3 text-emerald-400" /><span className="text-emerald-400">copied</span></>
            : <><Copy className="w-3 h-3" />copy</>
          }
        </button>
      </div>
      <Highlight theme={themes.nightOwl} code={block.code.trim()} language={block.language}>
        {({ className, style, tokens, getLineProps, getTokenProps }) => (
          <pre
            className={`${className} text-[13px] leading-relaxed p-4 overflow-x-auto`}
            style={{ ...style, background: '#0d1117', margin: 0 }}
          >
            {tokens.map((line, i) => (
              <div key={i} {...getLineProps({ line })}>
                <span className="select-none mr-4 text-[11px] text-jax-navy w-4 inline-block text-right">
                  {i + 1}
                </span>
                {line.map((token, key) => (
                  <span key={key} {...getTokenProps({ token })} />
                ))}
              </div>
            ))}
          </pre>
        )}
      </Highlight>
    </div>
  )
}

// ─── Full post renderer ───────────────────────────────────────────────────────

const PostRenderer: React.FC<{ post: Post }> = ({ post }) => (
  <article className="pt-10 space-y-10">

    {/* Intro */}
    <p className="text-jax-body text-base sm:text-lg leading-relaxed">
      {post.intro}
    </p>

    {/* Sections */}
    {post.sections.map((section, i) => (
      <section key={i}>
        <h2 className="text-white font-semibold text-base mb-3 tracking-tight">
          {section.heading}
        </h2>
        <div className="space-y-3">
          {section.body.map((para, j) => (
            <p key={j} className="text-jax-body text-sm sm:text-base leading-relaxed">
              {para}
            </p>
          ))}
        </div>
        {section.code && <CodeSnippet block={section.code} />}
        {section.note && (
          <div className="mt-4 border-l-2 border-jax-link pl-4">
            <p className="text-jax-subtle text-sm leading-relaxed font-mono">
              {section.note}
            </p>
          </div>
        )}
      </section>
    ))}

    {/* Closing */}
    <section className="border-t border-jax-navy pt-8">
      <p className="text-jax-body text-sm sm:text-base leading-relaxed">
        {post.closing}
      </p>
    </section>

  </article>
)

export default PostRenderer
